import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { AmplifyService } from 'src/app/services/amplify.service';
import { IconsComponent } from '../icons/icons.component';
import { Router, RouterLink } from '@angular/router';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RouterMatcherPipe } from './router-match.pipe';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { I18n } from 'aws-amplify/utils';
import { NotificationsService } from 'src/app/services/notifications.service';
import { CredentialsService } from 'src/app/services/webauthn/credentials.service';
import { LoggingService } from 'src/app/services/logging.service';
import { catchError, of, switchMap, take, timer } from 'rxjs';
import MemoryAdapter from 'src/app/services/webauthn/memory-adapter.service';
import { ClickOutsideDirective } from 'src/app/directives/click-outside.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'vti-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IconsComponent, RouterLink, RouterMatcherPipe, IconsComponent, ClickOutsideDirective],
})
export class HeaderComponent implements OnInit {
  public ngOnInit(): void {
    timer(0, 30000)
      .pipe(
        switchMap(() => this.notificationsService.fetchNewNotifications()),
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.logger.error({ message: $localize`Fetch new notifications error`, error });

          return of(0);
        }),
      )
      .subscribe((count) => this.newNotificationsCount.set(count));
    this.biometricEnable.update(() => !!this.memoryAdapter.getValue('biometric'));
  }

  public navigateToNotif(): void {
    this.router.navigate(['/notifications']);
  }

  public switchLanguage(language: string): void {
    if (isPlatformBrowser(this.platformId)) {
      I18n.setLanguage(language);
      window.location.replace(`/${language}/summary`);
    }

    this.languageVisible.update(() => false);
    this.languageMobileVisible.update(() => false);
    this.userDropdownVisible.update(() => false);
  }

  public triggerDropdown(): void {
    this.languageVisible.update(() => false);
    this.userDropdownVisible.update((value) => !value);
  }

  public triggerLanguage(): void {
    this.languageVisible.update(() => true);
  }

  public closeMenu(): void {
    this.closeDropdown();
    this.hideMobileMenus();
  }

  public closeDropdown(): void {
    this.languageVisible.update(() => false);
    this.userDropdownVisible.update(() => false);
  }

  public hideMobileMenus(): void {
    this.mobileMenuVisible.update(() => false);
    this.languageMobileVisible.update(() => false);
  }

  public triggerMobileLanguage(): void {
    this.languageMobileVisible.update(() => true);
  }

  public async logout(): Promise<void> {
    this.userDropdownVisible.update((value) => !value);
    this.amplifyService.signOut();

    this.permissionsService.cleanUp();

    await this.router.navigate(['/summary']);
  }

  public showMenu(): void {
    this.mobileMenuVisible.update((value) => !value);
  }

  public async activateBiometric(): Promise<void> {
    this.hideMobileMenus();
    this.closeDropdown();
    try {
      const user = await this.credentialsService.getUserCredential();

      if (!user) {
        throw new Error($localize`User not found`);
      }

      this.credentialsService
        .registerUser({
          name: user.username,
          username: user.username,
          password: user.password,
        })
        .pipe(
          take(1),
          catchError((error) => {
            this.logger.error({ message: $localize`Register user error`, error });
            return of({ status: '' });
          }),
        )
        .subscribe((response) => {
          if (response.status === 'ok') {
            this.biometricEnable.update(() => true);
            this.credentialsService.activateBiometric();
          }
        });
    } catch (error: any) {
      this.logger.error({ message: error.message, error });
    }
  }

  private readonly platformId = inject(PLATFORM_ID);
  private readonly amplifyService = inject(AmplifyService);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly utilitiesService = inject(UtilitiesService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly memoryAdapter = inject(MemoryAdapter);
  private readonly logger = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);

  public userDropdownVisible = signal<boolean>(false);
  public languageVisible = signal<boolean>(false);
  public mobileMenuVisible = signal<boolean>(false);
  public languageMobileVisible = signal<boolean>(false);
  public biometricEnable = signal<boolean>(false);
  public currentRoute = this.utilitiesService.currentRoute;
  public fullName = this.amplifyService.currentUserFullName;
  public userEmail = signal<string>(this.amplifyService.attributeValue('email'));
  public newNotificationsCount = signal<number>(0);
}
